import React from "react";

import { Box, Typography, alpha, useTheme } from "@mui/material";
import { PencilIcon, type LucideIcon } from "lucide-react";

/**
 * ── THE INLINE EDIT AFFORDANCE ──────────────────────────────────────────────────────
 *
 * A handle on a VALUE: "edit this thing you are looking at", sitting immediately beside
 * the thing. Nothing is drawn at rest — no border, no fill — so the control does not
 * compete with the data it edits.
 *
 * ## Why this is not a `Button`
 *
 * `@repo/ui`'s `Button` is the right default for a page or card ACTION, and this is not
 * one. Rendered as `variant="secondary"` it came out as a 40px bordered control sitting
 * next to a one-line date inside an `InfoItem` — a chassis with a border and a fill,
 * physically larger than the value it operates on, so the eye landed on the affordance
 * rather than on the data. The button was louder than its own subject.
 *
 * This is the same argument `DisclosureTextButton` makes for a disclosure trigger, and
 * the same one `ViewToggle` makes for card furniture: the VOLUME of a control is part of
 * what it says, and a control subordinate to its neighbour has to be drawn that way.
 *
 * ## THE 32px HEIGHT IS DELIBERATE — it is NOT a working-conventions §60 violation
 *
 * 🚨 Read this before "fixing" it. §60 says the shared `Button` is never `size="small"`,
 * on the reasoning that a shrunken button "stops reading as an action… reads as a caption
 * that happens to be clickable, and makes the surface it sits on look subordinate to the
 * surfaces around it — which is backwards when the button IS that surface's action."
 *
 * Both halves of that rationale are about a control that is its surface's ACTION, and
 * neither transfers here:
 *
 *   * This control is subordinate BY DESIGN. It is a handle on one field, not the page's
 *     action, so "reads as subordinate" is the intended reading rather than the defect.
 *   * §60 also objects that a small button introduces a fourth height into the bands §29
 *     fixes (page header 40, table toolbar 49, fields 49). Those bands govern controls
 *     that share a ROW with each other or with a field. This one shares its row with a
 *     line of `body3` text inside an `InfoItem`, which is a different neighbour entirely
 *     — the same "pick by what the control ANSWERS TO" reasoning `ViewToggle` uses to
 *     justify taking its height as a prop.
 *
 * And §60 governs the shared `Button` component; this is not one, so the rule does not
 * literally bind it. The 32 is stated here as an argument rather than inherited as a
 * default, which is what that rule actually asks for.
 *
 * **32 also clears WCAG 2.5.8 (Target Size, Minimum), which requires 24×24 CSS px.** The
 * width is larger still, since the control sizes to its icon + label. Going smaller than
 * 32 would start trading against that floor, so this is not a number to shave further.
 *
 * ## The focus ring is the ONLY affordance at rest, so it is mandatory
 *
 * With no border and no fill, a keyboard user has nothing else to tell them where they
 * are — there is no chassis to highlight. So `:focus-visible` draws a real ring, and it
 * must never be dropped or replaced by a colour change alone (at these sizes a colour
 * shift on a 14px glyph is not a reliable focus indicator).
 *
 * `focus-visible` rather than `focus`, so a mouse click does not leave a ring behind.
 *
 * ## The hover/focus values are ViewToggle's, on purpose
 *
 * `alpha(text.primary, 0.06)` for the wash and `2px solid brand[600]` at `outlineOffset:
 * 1` for the ring are lifted verbatim from `apps/platform`'s `ViewToggle`, this codebase's
 * existing low-emphasis control. Copying rather than re-picking makes the two read as one
 * CLASS of control instead of two independent inventions — which is the whole point of
 * having a shared layer.
 *
 * ⚠️ Known divergence, not resolved here: `DisclosureTextButton` (also low-emphasis, also
 * `apps/platform`) rings at `brand[500]` with `outlineOffset: 2` and uses no hover wash at
 * all. So the app currently has two low-emphasis focus treatments. This component follows
 * `ViewToggle`; reconciling the two is its own change.
 *
 * ## Layer 1: absolute px, never `theme.spacing()` or a radius multiplier
 *
 * This renders under whichever app's theme mounts it, and the two disagree (§47/§29):
 * `@repo/ui` spaces on a 4px base, `apps/app` on MUI's default 8px, so any multiplier
 * renders at DOUBLE size in one of them. The same trap applies to `sx`'s `borderRadius`,
 * which multiplies `theme.shape.borderRadius` — 4 in `@repo/ui` but the tenant's own brand
 * token in `apps/app` (10 by default), the exact bug `ToggleButton`'s `TRACK_RADIUS_CEILING`
 * exists to work around. Every dimension below is therefore a literal pixel value.
 */

/** Hit-box height. See the docblock — this is an argued value, not a default. */
const HEIGHT = 32;
/** Sized to sit with `body3` (14px) without out-weighing it. */
const ICON_SIZE = 14;
const GAP = 6;
const PADDING_X = 8;
/**
 * Absolute, not `borderRadius: n` — that multiplies `theme.shape.borderRadius`, which is
 * the tenant's brand token in `apps/app`. See the docblock's layer-1 note.
 */
const RADIUS = 6;

export interface InlineEditButtonProps {
  /**
   * The visible text, and the accessible name unless `aria-label` overrides it.
   *
   * Name the ACTION, not the field — "Edit" beside a labelled value, not "Edit
   * manufacture date", which repeats the label already sitting above it. Where the
   * surrounding label is not enough on its own for a screen-reader user hearing the
   * button out of context, pass `aria-label` with the fuller phrasing and leave the
   * visible word short.
   */
  label: string;
  onClick: () => void;
  /**
   * Overrides the default pencil.
   *
   * A COMPONENT, not a rendered element: this control owns `size` and `strokeWidth` so
   * every instance is weighted identically. Typed `LucideIcon` rather than a structural
   * `ComponentType<…>` for the reason `ViewToggle` documents — a lucide glyph is a
   * `ForwardRefExoticComponent`, which is not assignable to `ComponentType` however
   * loosely its props are described.
   */
  icon?: LucideIcon;
  /**
   * Dimmed and non-interactive.
   *
   * Load-bearing for `PermissionGate`, which clones its child with `disabled: true` and
   * relies on the NATIVE disabled attribute suppressing hover so its wrapping span can be
   * the tooltip's hover target. A component that only faked disablement visually would
   * silently break that tooltip — and a disabled control with no reachable explanation is
   * exactly what working-conventions §46 forbids.
   */
  disabled?: boolean;
  /** Accessible name, when the visible `label` alone would be ambiguous out of context. */
  "aria-label"?: string;
}

export const InlineEditButton = ({
  label,
  onClick,
  icon: Icon = PencilIcon,
  disabled = false,
  "aria-label": ariaLabel,
}: InlineEditButtonProps) => {
  const theme = useTheme();

  return (
    <Box
      component="button"
      // Never `submit` — this sits inside record forms, and the UA default would post one.
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      sx={{
        // The UA button chassis, removed. Everything visual below is this component's own.
        // Without `font: inherit` the UA supplies its own 13.333px face and the label would
        // not match the `body3` value it sits beside.
        appearance: "none",
        font: "inherit",
        margin: 0,
        border: "none",
        backgroundColor: "transparent",
        cursor: disabled ? "default" : "pointer",

        height: HEIGHT,
        px: `${PADDING_X}px`,
        display: "inline-flex",
        alignItems: "center",
        gap: `${GAP}px`,
        borderRadius: `${RADIUS}px`,
        flexShrink: 0,

        color: theme.palette.text.secondary,
        opacity: disabled ? 0.45 : 1,

        // A transient wash, never a resting background: a resting one would re-introduce
        // the drawn box this control exists to avoid. Suppressed while disabled, where a
        // hover response on something that will not act reads as a bug.
        ...(disabled
          ? {}
          : {
              "&:hover": {
                color: theme.palette.text.primary,
                backgroundColor: alpha(theme.palette.text.primary, 0.06),
              },
            }),

        // MANDATORY. With nothing drawn at rest this is the only thing making the control
        // keyboard-usable — see the docblock.
        "&:focus-visible": {
          color: theme.palette.text.primary,
          outline: `2px solid ${theme.palette.brand[600]}`,
          outlineOffset: 1,
        },
      }}
    >
      <Icon size={ICON_SIZE} strokeWidth={1} />
      <Typography
        component="span"
        sx={{
          // `typography: 'body3'` rather than `variant="body3"`, and this is a TYPING
          // workaround with a real upside — do not "simplify" it back.
          //
          // `body3` is a THEME AUGMENTATION, not a stock MUI variant. When `apps/platform`'s
          // tsconfig compiles this package's files, that augmentation is not in scope, so
          // `variant="body3"` fails to typecheck (TS2769, "Did you mean body1?"). The same
          // error already sits on `Input.tsx`'s helper text for exactly this reason — a
          // pre-existing repo-level gap this component deliberately does not add a second
          // instance of.
          //
          // Hardcoding 14px would compile but be WRONG: `@repo/ui` sets body3 to 14px while
          // `apps/app` sets it from its own `font.label` token, so a literal would silently
          // desync the label from its theme in one of the two apps. The `sx` form keeps the
          // real theme lookup — identical rendering, no literal, no error.
          typography: "body3",
          // `inherit` so the button's own rest/hover/focus colour reaches the label instead
          // of being overridden by a second, static one.
          color: "inherit",
          whiteSpace: "nowrap",
          lineHeight: 1,
        }}
      >
        {label}
      </Typography>
    </Box>
  );
};
